import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  listOrganizations,
  listAllUsers,
  OrganizationPlan,
  deleteOrganization,
  OrganizationAdminSummary,
  OrganizationAdminUser,
} from '../../services/AdminOrganizations';
import { extendTrial } from '../../services/Trials';
import { deleteUser, moveUser, triggerUserPasswordReset } from '../../services/User';
import { createOrganization } from '../../services/Organization';
import { resendEmail } from '../../services/Authentication';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import PlanBadge from './PlanBadge';
import InviteUserDialog from './InviteUserDialog';
import { formatSek, formatUsdTitle } from './usage/sek';
import {
  ArrowRightLeft,
  ChevronDown,
  ChevronRight,
  KeyRound,
  Loader2,
  Mail,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  Trash2,
  UserPlus,
} from 'lucide-react';

type PlanFilter = 'all' | OrganizationPlan;

const QUERI_ORGANIZATION_ID = '40b2618d-92dd-45a5-af79-14cb7d0d1023';
const INTERNAL_DOMAINS = ['queri.law', 'queri.ai', 'queri.se'];

const isInternal = (email: string) =>
  INTERNAL_DOMAINS.includes(email.toLowerCase().split('@')[1] ?? '');

const formatTokens = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return String(n);
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const daysLeft = (iso: string | null): number | null =>
  iso ? Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000) : null;

const fullName = (u: OrganizationAdminUser) =>
  `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();

const userMatches = (u: OrganizationAdminUser, q: string) =>
  u.email.toLowerCase().includes(q) ||
  fullName(u).toLowerCase().includes(q) ||
  (u.company ?? '').toLowerCase().includes(q);

/**
 * Every organization with its users (Queri staff). Replaces the separate
 * Users and Trial users pages: filter by plan, search orgs and people, and
 * open an organization to manage it.
 */
const OrganizationsDashboard: React.FC = () => {
  const [orgs, setOrgs] = useState<OrganizationAdminSummary[]>([]);
  const [users, setUsers] = useState<OrganizationAdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState<PlanFilter>('all');
  const [hideInternal, setHideInternal] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [extending, setExtending] = useState<string | null>(null);

  const [pendingOrgDelete, setPendingOrgDelete] = useState<OrganizationAdminSummary | null>(null);
  const [confirmName, setConfirmName] = useState('');
  const [deletingOrg, setDeletingOrg] = useState(false);

  const [inviteOrgId, setInviteOrgId] = useState<string | null | undefined>(undefined);
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [creatingOrg, setCreatingOrg] = useState(false);

  const [pendingUserDelete, setPendingUserDelete] = useState<OrganizationAdminUser | null>(null);
  const [userToMove, setUserToMove] = useState<OrganizationAdminUser | null>(null);
  const [moveTarget, setMoveTarget] = useState<string>('');
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    const [orgRes, userRes] = await Promise.all([listOrganizations(), listAllUsers()]);
    if (orgRes.success && orgRes.data) setOrgs(orgRes.data);
    else toast.error('Failed to load organizations');
    if (userRes.success && userRes.data) setUsers(userRes.data);
    else toast.error('Failed to load users');
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchAll().finally(() => setLoading(false));
  }, [fetchAll]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    await fetchAll();
    setRefreshing(false);
  }, [fetchAll]);

  const usersByOrg = useMemo(() => {
    const map = new Map<string, OrganizationAdminUser[]>();
    for (const u of users) {
      if (hideInternal && isInternal(u.email)) continue;
      const list = map.get(u.organizationId) ?? [];
      list.push(u);
      map.set(u.organizationId, list);
    }
    return map;
  }, [users, hideInternal]);

  const counts = useMemo(() => {
    const byPlan = (p: OrganizationPlan) => orgs.filter((o) => o.plan === p).length;
    return {
      all: orgs.length,
      trial: byPlan('trial'),
      active: byPlan('active'),
      paused: byPlan('paused'),
      cancelled: byPlan('cancelled'),
    };
  }, [orgs]);

  const q = search.trim().toLowerCase();

  // Rows to show: orgs matching the filters, each with the users to list
  const rows = useMemo(() => {
    return orgs
      .filter((o) => planFilter === 'all' || o.plan === planFilter)
      .filter((o) => !(hideInternal && o.id === QUERI_ORGANIZATION_ID))
      .map((o) => {
        const orgUsers = usersByOrg.get(o.id) ?? [];
        if (!q) return { org: o, users: orgUsers, matchedUsers: false };
        const orgMatches =
          o.name.toLowerCase().includes(q) || (o.displayName ?? '').toLowerCase().includes(q);
        const matching = orgUsers.filter((u) => userMatches(u, q));
        if (orgMatches) return { org: o, users: orgUsers, matchedUsers: false };
        if (matching.length) return { org: o, users: matching, matchedUsers: true };
        return null;
      })
      .filter((r): r is NonNullable<typeof r> => r !== null)
      .sort((a, b) => a.org.name.localeCompare(b.org.name));
  }, [orgs, usersByOrg, planFilter, hideInternal, q]);

  const shownUsers = rows.reduce((n, r) => n + r.users.length, 0);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allExpanded = rows.length > 0 && rows.every((r) => expanded.has(r.org.id));
  const toggleAll = () =>
    setExpanded(allExpanded ? new Set() : new Set(rows.map((r) => r.org.id)));

  const handleExtend = async (org: OrganizationAdminSummary) => {
    setExtending(org.id);
    try {
      const res = await extendTrial(org.id, 7);
      if (res.success) {
        toast.success(`Trial extended by 7 days for ${org.name}`);
        await fetchAll();
      } else {
        toast.error('Failed to extend trial');
      }
    } finally {
      setExtending(null);
    }
  };

  const handleDeleteOrg = async () => {
    if (!pendingOrgDelete) return;
    setDeletingOrg(true);
    try {
      const res = await deleteOrganization(pendingOrgDelete.id);
      if (res.success) {
        toast.success(`Deleted "${pendingOrgDelete.name}"`);
        setPendingOrgDelete(null);
        setConfirmName('');
        await fetchAll();
      } else {
        toast.error(res.errors?.[0]?.title || 'Failed to delete organization');
      }
    } finally {
      setDeletingOrg(false);
    }
  };

  const handleCreateOrg = async () => {
    if (!newOrgName.trim()) return;
    setCreatingOrg(true);
    try {
      const res = await createOrganization(newOrgName.trim());
      if (res.success) {
        toast.success(`Created "${newOrgName.trim()}"`);
        setCreateOrgOpen(false);
        setNewOrgName('');
        await fetchAll();
      } else {
        toast.error(res.errors?.[0]?.title || 'Failed to create organization');
      }
    } finally {
      setCreatingOrg(false);
    }
  };

  const handlePasswordReset = async (u: OrganizationAdminUser) => {
    const res = await triggerUserPasswordReset(u.id);
    if (res.success) toast.success(`Password reset email sent to ${u.email}`);
    else toast.error(res.errors?.[0]?.title || 'Failed to send password reset email');
  };

  const handleResendConfirmation = async (u: OrganizationAdminUser) => {
    const res = await resendEmail(u.id);
    if (res.success) toast.success(`Sign-up email sent again to ${u.email}`);
    else toast.error('Failed to send the email');
  };

  const handleDeleteUser = async () => {
    if (!pendingUserDelete) return;
    setBusy(true);
    try {
      const res = await deleteUser(pendingUserDelete.id);
      if (res.success) {
        toast.success(`Deleted ${pendingUserDelete.email}`);
        setPendingUserDelete(null);
        await fetchAll();
      } else {
        toast.error(res.errors?.[0]?.title || 'Failed to delete user');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleMoveUser = async () => {
    if (!userToMove || !moveTarget) return;
    setBusy(true);
    try {
      const res = await moveUser(userToMove.id, moveTarget);
      if (res.success) {
        const target = orgs.find((o) => o.id === moveTarget)?.name ?? 'the organization';
        toast.success(`Moved ${userToMove.email} to ${target}`);
        setUserToMove(null);
        setMoveTarget('');
        setExpanded((prev) => new Set(prev).add(moveTarget));
        await fetchAll();
      } else {
        toast.error(res.errors?.[0]?.title || 'Failed to move user');
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Organizations</h1>
          <p className="text-sm text-muted-foreground">
            {rows.length} organizations, {shownUsers} users. Open an organization to see its
            users; search finds organizations and people.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setInviteOrgId(null)}>
            <UserPlus className="h-4 w-4" />
            Invite user
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCreateOrgOpen(true)}>
            <Plus className="h-4 w-4" />
            New organization
          </Button>
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[240px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search organization, email, name or company…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <Select value={planFilter} onValueChange={(v) => setPlanFilter(v as PlanFilter)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ({counts.all})</SelectItem>
              <SelectItem value="active">Active ({counts.active})</SelectItem>
              <SelectItem value="trial">Trial ({counts.trial})</SelectItem>
              <SelectItem value="paused">Paused ({counts.paused})</SelectItem>
              <SelectItem value="cancelled">Cancelled ({counts.cancelled})</SelectItem>
            </SelectContent>
          </Select>
          <label className="flex select-none items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={hideInternal}
              onChange={(e) => setHideInternal(e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            Hide Queri staff and testers
          </label>
          <Button variant="ghost" size="sm" onClick={toggleAll} disabled={rows.length === 0}>
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Organization</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Ends</TableHead>
              <TableHead>Users</TableHead>
              <TableHead>Tokens</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                  No organizations or users match the current filter.
                </TableCell>
              </TableRow>
            ) : (
              rows.map(({ org: o, users: orgUsers, matchedUsers }) => {
                const isQueri = o.id === QUERI_ORGANIZATION_ID;
                // A search that found people opens their organization
                const open = expanded.has(o.id) || matchedUsers;
                const left = o.plan === 'trial' ? daysLeft(o.planEndsAt ?? o.trialEndsAt) : null;
                const overLimit = o.tokenLimit != null && o.tokensConsumed >= o.tokenLimit;
                return (
                  <React.Fragment key={o.id}>
                    <TableRow className="cursor-pointer" onClick={() => toggle(o.id)}>
                      <TableCell className="w-8 pr-0">
                        {open ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {o.name}
                          {isQueri && (
                            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-blue-800">
                              Queri
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <PlanBadge plan={o.plan} planEndsAt={o.planEndsAt} />
                          {o.isWorkspace && (
                            <span
                              className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground"
                              title={o.slug ? `${o.slug}.queri.law` : undefined}
                            >
                              Workspace
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {o.planEndsAt ? (
                          <span>
                            {formatDate(o.planEndsAt)}
                            {left != null && (
                              <span className={`ml-1.5 text-xs ${left <= 2 ? 'text-red-600' : 'text-muted-foreground'}`}>
                                {left > 0 ? `${left}d left` : 'expired'}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Open-ended</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {o.seatLimit != null ? `${o.userCount} / ${o.seatLimit}` : o.userCount}
                      </TableCell>
                      <TableCell className={`text-sm ${overLimit ? 'text-red-600' : ''}`}>
                        {o.tokenLimit != null
                          ? `${formatTokens(o.tokensConsumed)} / ${formatTokens(o.tokenLimit)}`
                          : formatTokens(o.tokensConsumed)}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          {o.isTrial && !isQueri && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={extending === o.id}
                              onClick={() => void handleExtend(o)}
                              title="Extend this trial by 7 days"
                            >
                              {extending === o.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Plus className="h-3 w-3" />
                              )}
                              7 days
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setInviteOrgId(o.id)}
                            title="Invite someone to this organization"
                          >
                            <UserPlus className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`/admin/organizations/${o.id}`}>
                              <Settings2 className="h-3.5 w-3.5" />
                              Manage
                            </Link>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={isQueri}
                            onClick={() => {
                              setPendingOrgDelete(o);
                              setConfirmName('');
                            }}
                            title={
                              isQueri
                                ? "Queri's own organization cannot be deleted"
                                : 'Delete this organization and its users'
                            }
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>

                    {open && (
                      <TableRow className="bg-muted/20 hover:bg-muted/20">
                        <TableCell />
                        <TableCell colSpan={6} className="py-2">
                          {orgUsers.length === 0 ? (
                            <p className="py-2 text-sm text-muted-foreground">No users.</p>
                          ) : (
                            <UsersTable
                              users={orgUsers}
                              onPasswordReset={handlePasswordReset}
                              onResendConfirmation={handleResendConfirmation}
                              onMove={(u) => {
                                setUserToMove(u);
                                setMoveTarget('');
                              }}
                              onDelete={setPendingUserDelete}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <InviteUserDialog
        open={inviteOrgId !== undefined}
        organizationId={inviteOrgId ?? null}
        organizations={orgs}
        onClose={() => setInviteOrgId(undefined)}
        onInvited={() => void fetchAll()}
      />

      <Dialog open={createOrgOpen} onOpenChange={setCreateOrgOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New organization</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleCreateOrg();
            }}
          >
            <Input
              autoFocus
              placeholder="Organization name"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
            />
          </form>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOrgOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void handleCreateOrg()} disabled={!newOrgName.trim() || creatingOrg}>
              {creatingOrg && <Loader2 className="h-3 w-3 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={userToMove != null} onOpenChange={(o) => !o && setUserToMove(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move {userToMove?.email}</DialogTitle>
          </DialogHeader>
          <Select value={moveTarget} onValueChange={setMoveTarget}>
            <SelectTrigger>
              <SelectValue placeholder="Select an organization" />
            </SelectTrigger>
            <SelectContent>
              {orgs
                .filter((o) => o.id !== userToMove?.organizationId)
                .map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserToMove(null)}>
              Cancel
            </Button>
            <Button onClick={() => void handleMoveUser()} disabled={!moveTarget || busy}>
              {busy && <Loader2 className="h-3 w-3 animate-spin" />}
              Move
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pendingUserDelete != null}
        onOpenChange={(o) => !o && setPendingUserDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{pendingUserDelete?.email}</b> will no longer be able to sign in. Their data is
              kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                void handleDeleteUser();
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingOrgDelete != null}
        onOpenChange={(o) => {
          if (!o) {
            setPendingOrgDelete(null);
            setConfirmName('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete organization?</AlertDialogTitle>
            <AlertDialogDescription>
              This will soft-delete <b>{pendingOrgDelete?.name}</b> and all{' '}
              {pendingOrgDelete?.userCount ?? 0} of its users. New sign-ins will be blocked. Type
              the organization name to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <input
            type="text"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={pendingOrgDelete?.name ?? ''}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingOrg}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmName.trim() !== pendingOrgDelete?.name || deletingOrg}
              onClick={(e) => {
                e.preventDefault();
                void handleDeleteOrg();
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deletingOrg ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

interface UsersTableProps {
  users: OrganizationAdminUser[];
  onPasswordReset: (u: OrganizationAdminUser) => void;
  onResendConfirmation: (u: OrganizationAdminUser) => void;
  onMove: (u: OrganizationAdminUser) => void;
  onDelete: (u: OrganizationAdminUser) => void;
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  onPasswordReset,
  onResendConfirmation,
  onMove,
  onDelete,
}) => (
  <Table>
    <TableHeader>
      <TableRow className="hover:bg-transparent">
        <TableHead>User</TableHead>
        <TableHead>Job title</TableHead>
        <TableHead>Team</TableHead>
        <TableHead>Hrs/wk</TableHead>
        <TableHead>Meetings</TableHead>
        <TableHead>Signed up</TableHead>
        <TableHead>Last active</TableHead>
        <TableHead>Chats</TableHead>
        <TableHead>Cost 30d</TableHead>
        <TableHead className="w-10" />
      </TableRow>
    </TableHeader>
    <TableBody>
      {users.map((u) => {
        const name = fullName(u);
        return (
          <TableRow key={u.id} className="hover:bg-transparent">
            <TableCell>
              <div className="text-sm font-medium">{name || u.email}</div>
              {name && <div className="text-xs text-muted-foreground">{u.email}</div>}
              <div className="mt-0.5 flex flex-wrap gap-1">
                {u.status !== 'ACTIVE' && (
                  <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                    {u.status === 'UNCONFIRMED' ? 'Invited' : u.status}
                  </span>
                )}
                {u.role === 'admin' && (
                  <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-800">
                    Admin
                  </span>
                )}
                {u.hiddenFromMembers && (
                  <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">
                    Hidden
                  </span>
                )}
                {isInternal(u.email) && (
                  <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">
                    Internal
                  </span>
                )}
              </div>
            </TableCell>
            <TableCell className="text-sm">
              {u.jobTitle || '-'}
              {u.company && <div className="text-xs text-muted-foreground">{u.company}</div>}
            </TableCell>
            <TableCell className="text-sm">{u.teamSize || '-'}</TableCell>
            <TableCell className="text-sm">{u.legalHoursPerWeek ?? '-'}</TableCell>
            <TableCell className="text-sm">
              {u.meetingAssistantEnabled ? (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                  On
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">Off</span>
              )}
            </TableCell>
            <TableCell className="text-sm">{formatDate(u.createdAt)}</TableCell>
            <TableCell className="text-sm">{formatDate(u.lastActiveAt)}</TableCell>
            <TableCell className="text-sm">{u.chatCount}</TableCell>
            <TableCell className="text-sm" title={formatUsdTitle(u.costUsd30d)}>
              {formatSek(u.costSek30d)}
            </TableCell>
            <TableCell>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" aria-label={`Actions for ${u.email}`}>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {u.status === 'UNCONFIRMED' ? (
                    <DropdownMenuItem onClick={() => onResendConfirmation(u)}>
                      <Mail className="h-4 w-4" />
                      Resend sign-up email
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => onPasswordReset(u)}>
                      <KeyRound className="h-4 w-4" />
                      Send password reset
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => onMove(u)}>
                    <ArrowRightLeft className="h-4 w-4" />
                    Move to organization…
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDelete(u)}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete user
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  </Table>
);

export default OrganizationsDashboard;
